import SEO from '../components/SEO';
import React from 'react';
import { Shield, FileText, Lock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const LegalNotice = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Mentions légales et RGPD | DEBECO"
        description="Mentions légales, politique de confidentialité et protection des données personnelles de DEBECO."
        canonical="/mentions-legales"
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Mentions légales & RGPD</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Informations légales et protection des données personnelles
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Mentions légales */}
            <Card className="border-2">
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-white" />
                  <h2 className="text-2xl font-bold text-white">Mentions légales</h2>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="space-y-6 text-gray-700">
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Éditeur du site</h3>
                    <p><strong>DEBECO</strong></p>
                    <p>Entreprise de débarras et recyclage</p>
                    <p>Adresse : 4 Rue des Frères de Montgolfier - 74600 ANNECY</p>
                    <p>Téléphone : 04 51 40 16 17</p>
                    <p>Email : contact@debeco.fr</p>
                    <p className="mt-2">SIRET : 52526216800020</p>
                    <p>TVA : FR79525262168</p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Hébergement</h3>
                    <p><strong>Emergent</strong></p>
                    <p>Plateforme de déploiement d'applications web</p>
                    <p>Site : emergentagent.com</p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Propriété intellectuelle</h3>
                    <p>
                      L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle. Tous les droits de reproduction sont réservés.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Crédits</h3>
                    <p>Photos : DEBECO, Unsplash, Pexels</p>
                    <p>Design et développement : DEBECO</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* RGPD */}
            <Card className="border-2">
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-white" />
                  <h2 className="text-2xl font-bold text-white">Protection des données personnelles (RGPD)</h2>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="space-y-6 text-gray-700">
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Collecte des données</h3>
                    <p>
                      Dans le cadre de l'utilisation du site DEBECO, nous sommes amenés à collecter des données personnelles vous concernant. Ces données sont collectées uniquement via le formulaire de contact et le module de devis.
                    </p>
                    <p className="mt-2"><strong>Données collectées :</strong></p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Nom et prénom</li>
                      <li>Adresse email</li>
                      <li>Numéro de téléphone</li>
                      <li>Ville</li>
                      <li>Message et informations sur votre demande</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Finalité de la collecte</h3>
                    <p>
                      Les données collectées sont utilisées exclusivement pour :
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Répondre à vos demandes de devis</li>
                      <li>Vous contacter concernant nos services</li>
                      <li>Assurer le suivi de votre demande</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Durée de conservation</h3>
                    <p>
                      Vos données personnelles sont conservées pendant la durée nécessaire au traitement de votre demande, puis archivées conformément aux obligations légales.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Vos droits</h3>
                    <p>
                      Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Droit d'accès à vos données</li>
                      <li>Droit de rectification</li>
                      <li>Droit à l'effacement</li>
                      <li>Droit à la limitation du traitement</li>
                      <li>Droit à la portabilité</li>
                      <li>Droit d'opposition</li>
                    </ul>
                    <p className="mt-3">
                      Pour exercer ces droits, contactez-nous par email à : <strong>contact@debeco.fr</strong>
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Sécurité</h3>
                    <p>
                      DEBECO met en œuvre toutes les mesures techniques et organisationnelles afin d'assurer la sécurité des données personnelles et empêcher leur déformation, endommagement ou accès par des tiers non autorisés.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Cookies</h3>
                    <p>
                      Ce site n'utilise pas de cookies de suivi ou de publicité. Seuls des cookies techniques nécessaires au bon fonctionnement du site peuvent être utilisés.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="border-2 bg-gradient-to-br from-blue-50 to-emerald-50">
              <CardContent className="p-8 text-center">
                <Lock className="w-12 h-12 text-blue-900 mx-auto mb-4" />
                <h3 className="font-bold text-xl text-blue-900 mb-3">
                  Questions sur vos données ?
                </h3>
                <p className="text-gray-700 mb-4">
                  Pour toute question concernant vos données personnelles ou l'exercice de vos droits
                </p>
                <a 
                  href="mailto:contact@debeco.fr" 
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  contact@debeco.fr
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalNotice;
